import { BOTS, type CpuRecordEvent } from '@xox/shared'
import { Trophy } from 'lucide-react'
import { Confetti } from '../../ice/art/Confetti'
import { BOSS_THEME } from '../../lib/bossTheme'
import { useArena } from '../../store/arena'
import { Avatar } from '../Avatar'
import { RobotFace, robotPaletteFor } from '../art/RobotFace'

const COPY: Record<
  CpuRecordEvent['kind'],
  { eyebrow: (bot: string) => string; detail: (ev: CpuRecordEvent, bot: string) => string }
> = {
  longest: {
    eyebrow: () => 'New record',
    detail: (ev, bot) =>
      `${ev.moves} moves against ${bot}${ev.previous ? ` · beat ${ev.previous}` : ''}`,
  },
  draw: { eyebrow: () => 'Draw held', detail: (_ev, bot) => `held ${bot} to a draw` },
  win: { eyebrow: (bot) => `${bot} beaten`, detail: () => 'did the impossible' },
}

/** "Congrats, Ada!" — a human did something notable against a house bot. Tap to dismiss. */
export function RecordCard({ ev }: { ev: CpuRecordEvent }) {
  const dismiss = useArena((s) => s.dismiss)
  const theme = BOSS_THEME[ev.game]
  const bot = BOTS[ev.game]
  return (
    <div
      onClick={dismiss}
      onKeyDown={(e) => e.key === 'Enter' && dismiss()}
      role="button"
      tabIndex={0}
      className={`-m-3 cursor-pointer rounded-2xl p-3 text-white ${theme.hero} ${theme.ring}`}
    >
      {ev.kind !== 'longest' && <Confetti />}
      <div className="relative flex items-center gap-3">
        <div className="relative shrink-0">
          <RobotFace className="size-11" palette={robotPaletteFor(bot.id)} />
          <span className="absolute -right-1 -bottom-1 flex size-5 items-center justify-center rounded-full bg-amber-400 text-slate-900 shadow">
            <Trophy className="size-3" aria-hidden />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${theme.eyebrow}`}>
            {COPY[ev.kind].eyebrow(bot.nickname)}
          </div>
          <div className="truncate text-base font-black leading-tight">
            Congrats, {ev.player.nickname}!
          </div>
          <div className={`truncate text-xs ${theme.sub}`}>
            {COPY[ev.kind].detail(ev, bot.nickname)}
          </div>
        </div>
        <Avatar
          id={ev.player.id}
          avatar={ev.player.avatar}
          color={ev.player.color}
          size="md"
          className="ring-2 ring-white/30"
        />
      </div>
    </div>
  )
}
